import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { useAuth } from "@/contexts/AuthContext";
import { MARKETPLACE_CATEGORY_LABELS } from "@/lib/causes";
import { Search, Plus, Tag, LogIn } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

export default function Marketplace() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const category = params.get("category") || "all";

  useEffect(() => {
    const q = new URLSearchParams();
    if (category !== "all") q.set("category", category);
    if (search) q.set("search", search);
    api.get(`/marketplace/listings?${q.toString()}`).then(({ data }) => setListings(data));
  }, [category, search]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  return (
    <Layout>
      <div data-testid="marketplace-page" className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <span className="overline text-[var(--cm-primary)]">Marketplace</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">
              Spend your Time Hours.
            </h1>
            <p className="text-ink-soft mt-2 max-w-xl">
              Services and goods offered by other members. Everything is priced in hours.
            </p>
          </div>
          {isAuthed ? (
            <Link to="/marketplace/new" data-testid="market-create-listing-btn" className="btn-primary inline-flex items-center gap-2">
              <Plus className="h-4 w-4" /> Offer something
            </Link>
          ) : (
            <Link to="/register" data-testid="market-guest-cta" className="btn-primary inline-flex items-center gap-2">
              <LogIn className="h-4 w-4" /> Sign up to redeem
            </Link>
          )}
        </div>

        {!isAuthed && (
          <div
            data-testid="market-guest-banner"
            className="card-soft p-4 mb-6 flex flex-wrap items-center justify-between gap-3 border-l-4"
            style={{ borderLeftColor: "var(--cm-primary)" }}
          >
            <p className="text-sm text-ink-soft">
              Take a look around — <span className="text-ink font-semibold">create a free account</span> to redeem listings with Time Hours you've earned by volunteering.
            </p>
            <Link to="/login" className="text-sm font-semibold text-[var(--cm-primary)] underline underline-offset-4" data-testid="market-guest-signin">
              Sign in
            </Link>
          </div>
        )}

        <div className="card-soft p-5 grid md:grid-cols-12 gap-3 items-center mb-8">
          <div className="md:col-span-7 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
            <input
              data-testid="market-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search listings…"
              className="w-full pl-10 pr-4 py-2.5 bg-app rounded-xl border border-ink-soft focus:ring-2 focus:ring-[var(--cm-primary)]/40"
            />
          </div>
          <div className="md:col-span-5">
            <select
              data-testid="market-category-filter"
              value={category}
              onChange={(e) => setFilter("category", e.target.value)}
              className="w-full px-3 py-2.5 bg-app rounded-xl border border-ink-soft"
            >
              <option value="all">All categories</option>
              {Object.entries(MARKETPLACE_CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="card-soft p-12 text-center" data-testid="market-empty">
            <Tag className="h-6 w-6 mx-auto text-ink-soft" />
            <p className="font-display text-xl mt-3 font-semibold">Nothing here yet.</p>
            <p className="text-ink-soft mt-1">Be the first to offer something — your neighbors will thank you.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((l) => (
              <Link
                key={l.id}
                to={`/marketplace/${l.id}`}
                data-testid={`listing-card-${l.id}`}
                className="card-soft overflow-hidden hover:-translate-y-0.5 transition-transform group"
              >
                {l.image_url ? (
                  <div className="aspect-[4/3] overflow-hidden bg-app">
                    <img src={resolveImageUrl(l.image_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[var(--cm-accent)]/40 grid place-items-center font-display text-3xl text-[var(--cm-ink)]/40">
                    {l.title.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="pill">{MARKETPLACE_CATEGORY_LABELS[l.category] || l.category}</span>
                    <TimeBadge hours={l.price_hours} size="sm" />
                  </div>
                  <h3 className="font-display font-semibold leading-tight group-hover:text-[var(--cm-primary)] transition-colors">{l.title}</h3>
                  <p className="text-xs text-ink-soft mt-2 line-clamp-2">{l.description}</p>
                  <p className="text-xs text-ink-soft mt-3">by {l.seller_name}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
